import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import React from "react";
import { couleurs } from "../../theme/tokens";
import PageStatique from "../_Base/PageStatique";

/**
 * Politique de confidentialité — s'applique au site Web (liencitoyen.com et
 * les sites de chaque municipalité), à l'application mobile Lien Citoyen, et
 * aux accès donnés à des fournisseurs tiers de sites municipaux via « Se
 * connecter avec Lien Citoyen » (OAuth, phase 4-5 du plan API sites
 * municipaux). Texte identique pour toutes les villes : page de
 * PLATEFORME, pas propre à une municipalité (voir PageStatique).
 *
 * Révisée le 18 septembre 2026 pour corriger la section « Partage des
 * renseignements personnels » : la version précédente affirmait qu'aucun
 * renseignement n'était transmis à des tiers, ce qui n'est plus le cas
 * depuis l'ouverture de l'API (webhooks, consentement OAuth vers les sites
 * municipaux et leurs fournisseurs) — et n'était déjà plus tout à fait vrai
 * avec les sous-traitants historiques (SendGrid, Twilio, Firebase/Expo).
 * Ajout aussi d'une section propre à l'application mobile (permissions),
 * absente de la version d'origine.
 *
 * N'utilisait plus `Sidebar`/`Heroband` depuis le 18 septembre 2026 (soir) :
 * ces composants exigent toujours une municipalité résolue, ce qui rendait
 * cette page — et /support — inaccessibles sur liencitoyen.com (aucune
 * ville associée à ce domaine, par conception). Voir App.tsx.
 */
export default function PolitiqueConfidentialite() {

    return (
        <PageStatique>
            <Typography className='page-title' sx={{ fontWeight:'900', color:couleurs.BleuClair, lineHeight:'55px' }}>Politique de Confidentialité</Typography>
            <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Typography variant="subtitle1">
                            Cette politique s'applique au site Web Lien Citoyen, à l'application mobile
                            Lien Citoyen, et aux accès donnés à des sites Web municipaux tiers via
                            « Se connecter avec Lien Citoyen ».
                        </Typography>
                        <Typography variant="subtitle1">
                            Date de dernière mise à jour : 18 septembre 2026
                        </Typography>

                        <Typography variant="h5" sx={{pt:3}}>1. RENSEIGNEMENTS COLLECTÉS</Typography>
                        <Typography variant="body1">
                            Selon la façon dont vous utilisez Lien Citoyen, nous recueillons :
                            <ul>
                                <li>Nom, prénom</li>
                                <li>Adresse postale et code postal</li>
                                <li>Adresse courriel</li>
                                <li>Numéro de téléphone</li>
                                <li>Position géographique, lorsque vous l'autorisez (remplir automatiquement l'adresse d'un signalement)</li>
                                <li>Photos jointes à un signalement ou à votre profil</li>
                                <li>Contenu de vos demandes de permis, signalements et réponses aux sondages</li>
                                <li>Jeton de votre appareil, pour les notifications de l'application mobile</li>
                                <li>Vos préférences de notification (SMS, courriel, appel vocal, notification) et les types d'alertes auxquels vous êtes abonné(e)</li>
                            </ul>
                        </Typography>
                        <Typography variant="body1">
                            Ces renseignements sont recueillis lorsque vous créez un compte, remplissez un
                            formulaire, envoyez un signalement, déposez une demande de permis ou répondez
                            à un sondage.
                        </Typography>

                        <Typography variant="h5" sx={{pt:3}}>2. FINALITÉS D'UTILISATION</Typography>
                        <Typography variant="body1">
                            Nous utilisons vos renseignements personnels pour :
                            <ul>
                                <li>Créer et gérer votre compte citoyen</li>
                                <li>Vous envoyer les alertes de votre municipalité, selon les canaux choisis (SMS, courriel, appel vocal, notification)</li>
                                <li>Traiter vos demandes de permis et vos signalements, et vous permettre d'en suivre le statut</li>
                                <li>Vous permettre de répondre aux sondages de votre municipalité</li>
                                <li>Communiquer avec vous au sujet de votre compte</li>
                            </ul>
                        </Typography>

                        <Typography variant="h5" sx={{pt:3}}>3. PARTAGE DES RENSEIGNEMENTS PERSONNELS</Typography>
                        <Typography variant="body1">
                            Nous ne vendons jamais vos renseignements personnels et ne les utilisons pas
                            à des fins publicitaires. Ils sont partagés dans deux cas seulement.
                        </Typography>
                        <Typography variant="body1" sx={{pt:2}}>
                            <strong>a) Fournisseurs qui agissent pour notre compte</strong>
                        </Typography>
                        <Typography variant="body1">
                            Certaines tâches sont confiées à des fournisseurs qui traitent vos
                            renseignements en notre nom, selon nos instructions :
                        </Typography>
                        <table style={{ borderCollapse: 'collapse', width: '100%', maxWidth: '600px', marginTop: '8px', marginBottom: '16px' }}>
                            <thead>
                                <tr>
                                    <th style={{ textAlign: 'left', borderBottom: '2px solid ' + couleurs.GrisBordure, padding: '8px 16px 8px 0' }}>Fournisseur</th>
                                    <th style={{ textAlign: 'left', borderBottom: '2px solid ' + couleurs.GrisBordure, padding: '8px 0' }}>Rôle</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td style={{ padding: '8px 16px 8px 0', borderBottom: '1px solid ' + couleurs.GrisBordure }}>SendGrid (Twilio)</td>
                                    <td style={{ padding: '8px 0', borderBottom: '1px solid ' + couleurs.GrisBordure }}>Envoi des courriels</td>
                                </tr>
                                <tr>
                                    <td style={{ padding: '8px 16px 8px 0', borderBottom: '1px solid ' + couleurs.GrisBordure }}>Twilio</td>
                                    <td style={{ padding: '8px 0', borderBottom: '1px solid ' + couleurs.GrisBordure }}>Envoi des SMS et des appels vocaux d'alerte</td>
                                </tr>
                                <tr>
                                    <td style={{ padding: '8px 16px 8px 0', borderBottom: '1px solid ' + couleurs.GrisBordure }}>Firebase (Google) / Expo</td>
                                    <td style={{ padding: '8px 0', borderBottom: '1px solid ' + couleurs.GrisBordure }}>Envoi des notifications de l'application mobile</td>
                                </tr>
                                <tr>
                                    <td style={{ padding: '8px 16px 8px 0' }}>Amazon Web Services</td>
                                    <td style={{ padding: '8px 0' }}>Hébergement des fichiers (logos, photos, documents), région Canada</td>
                                </tr>
                            </tbody>
                        </table>
                        <Typography variant="body1" sx={{pt:1}}>
                            <strong>b) Un site Web municipal, avec votre consentement explicite</strong>
                        </Typography>
                        <Typography variant="body1">
                            Votre municipalité peut avoir son propre site Web, qui utilise
                            « Se connecter avec Lien Citoyen » pour vous permettre d'y déposer une
                            demande de permis ou un signalement, ou d'y consulter votre profil, sans
                            recréer de compte.
                        </Typography>
                        <Typography variant="body1">
                            Dans ce cas :
                            <ul>
                                <li>Vous voyez, avant d'accepter, la liste précise de ce que ce site pourra faire (par exemple « Déposer et suivre vos demandes de permis »)</li>
                                <li>Aucun renseignement n'est transmis sans votre accord</li>
                                <li>Vous pouvez retirer cet accès en tout temps, depuis la section « Sites connectés » de votre profil — le site cesse alors immédiatement d'avoir accès à votre compte</li>
                            </ul>
                        </Typography>

                        <Typography variant="h5" sx={{pt:3}}>4. DURÉE DE CONSERVATION</Typography>
                        <Typography variant="body1">
                            Nous conservons vos renseignements personnels durant toute la période où
                            votre compte est actif.
                        </Typography>
                        <Typography variant="body1">
                            L'accès accordé à un site municipal (section 3b) a sa propre durée : le
                            jeton de connexion dure 1 heure et se renouvelle automatiquement tant que
                            vous l'utilisez ; il cesse dès que vous retirez l'accès. Nous conservons un
                            registre des accès accordés et retirés, à titre de preuve de votre
                            consentement.
                        </Typography>

                        <Typography variant="h5" sx={{pt:3}}>5. HÉBERGEMENT DES RENSEIGNEMENTS</Typography>
                        <Typography variant="body1">
                            Notre site est exploité par Studio Cognitif inc., 710, rue Notre-Dame Sud,
                            Louiseville (Québec) J5V 1Y9 — 819 498-8377.
                        </Typography>
                        <Typography variant="body1">
                            Les renseignements que nous recueillons sont hébergés au Canada. Les
                            fournisseurs mentionnés à la section 3a) peuvent traiter certains
                            renseignements (l'envoi d'un courriel, d'un SMS ou d'une notification, par
                            exemple) à partir de serveurs situés à l'extérieur du Canada, dans le
                            respect de leurs propres engagements de confidentialité.
                        </Typography>

                        <Typography variant="h5" sx={{pt:3}}>6. APPLICATION MOBILE</Typography>
                        <Typography variant="body1">
                            L'application mobile Lien Citoyen demande les autorisations suivantes,
                            uniquement au moment où elles sont nécessaires :
                            <ul>
                                <li><strong>Caméra et photos</strong> — pour joindre une photo à un signalement ou choisir une photo de profil</li>
                                <li><strong>Position</strong> — pour remplir automatiquement l'adresse d'un signalement</li>
                                <li><strong>Notifications</strong> — pour vous envoyer les alertes de votre municipalité</li>
                            </ul>
                        </Typography>
                        <Typography variant="body1">
                            Vous pouvez refuser une autorisation ou la retirer en tout temps dans les
                            réglages de votre téléphone ; les fonctionnalités qui en dépendent
                            deviennent alors indisponibles, le reste de l'application continue de
                            fonctionner.
                        </Typography>

                        <Typography variant="h5" sx={{pt:3}}>7. VOS DROITS</Typography>
                        <Typography variant="body1">
                            Vous pouvez en tout temps :
                            <ul>
                                <li><strong>Consulter, corriger ou faire supprimer</strong> les renseignements personnels que nous détenons à votre sujet</li>
                                <li><strong>Vous opposer</strong> au traitement de vos renseignements, ou demander leur retrait (par exemple d'une liste de diffusion)</li>
                                <li><strong>Retirer l'accès</strong> que vous avez donné à un site municipal (section « Sites connectés » de votre profil)</li>
                                <li><strong>Demander la suppression</strong> de votre compte</li>
                            </ul>
                        </Typography>
                        <Typography variant="body1">
                            Pour exercer l'un de ces droits, écrivez-nous à info@liencitoyen.com.
                        </Typography>

                        <Typography variant="h5" sx={{pt:3}}>8. RESPONSABLE, SÉCURITÉ ET MODIFICATIONS</Typography>
                        <Typography variant="body1" sx={{pt:2}}>
                            <strong>Responsable de la protection des renseignements personnels</strong>
                        </Typography>
                        <Typography variant="body1">
                            Kevin Teasdale-Dubé est responsable de la protection des renseignements
                            personnels. Vous pouvez le joindre à info@liencitoyen.com.
                        </Typography>
                        <Typography variant="body1">
                            Il s'engage à protéger vos renseignements, à respecter les finalités pour
                            lesquelles ils ont été recueillis, et à vous informer sans délai en cas
                            d'incident de confidentialité touchant vos renseignements.
                        </Typography>
                        <Typography variant="body1" sx={{pt:2}}>
                            <strong>Sécurité</strong>
                        </Typography>
                        <Typography variant="body1">
                            Vos renseignements sont protégés par le chiffrement des communications
                            (HTTPS/TLS), une gestion des accès limitée aux personnes autorisées, et des
                            mots de passe qui répondent à des exigences minimales de robustesse. Aucun
                            système n'offre une sécurité absolue.
                        </Typography>
                        <Typography variant="body1" sx={{pt:2}}>
                            <strong>Modifications de cette politique</strong>
                        </Typography>
                        <Typography variant="body1">
                            Cette politique peut être mise à jour ; la date de la dernière version
                            apparaît en haut de la page. Nous vous invitons à la consulter
                            périodiquement à{" "}
                            <a href="https://liencitoyen.com/politique-confidentialite">liencitoyen.com/politique-confidentialite</a>.
                        </Typography>
                        <Typography variant="body1" sx={{pt:2}}>
                            <strong>Acceptation</strong>
                        </Typography>
                        <Typography variant="body1">
                            En utilisant le site Web ou l'application mobile Lien Citoyen, vous
                            acceptez les conditions décrites dans cette politique.
                        </Typography>

                        <Typography variant="h5" sx={{pt:3}}>9. LOIS APPLICABLES</Typography>
                        <Typography variant="body1">
                            Nous respectons :
                            <ul>
                                <li>La <em>Loi sur la protection des renseignements personnels dans le secteur privé</em> (Québec), telle que modifiée par la Loi 25</li>
                                <li>La <em>Loi sur la protection des renseignements personnels et les documents électroniques</em> (Canada)</li>
                            </ul>
                        </Typography>
                    </Grid>
                </Grid>
            </PageStatique>
    );
}
